import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QmsUserSelect } from '../shared/QmsUserSelect';
import { useQmsUserById } from '../../hooks/useQmsUsers';
import type { QmsUser } from '../../types/qms.types';
import { extractId } from '../../types/qms.types';

function MemberChip({ id, onRemove }: { id: string; onRemove: () => void }) {
  const { data: user } = useQmsUserById(id);
  const label = user
    ? `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email
    : id;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-sm text-slate-700">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="text-slate-400 hover:text-red-600">
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

interface QmsCcbMembersEditorProps {
  memberIds: string[];
  onChange: (memberIds: string[]) => void;
}

export function QmsCcbMembersEditor({ memberIds, onChange }: QmsCcbMembersEditorProps) {
  const [selected, setSelected] = useState<QmsUser>();

  const addSelected = () => {
    const id = extractId(selected);
    if (!id || memberIds.includes(id)) return;
    onChange([...memberIds, id]);
    setSelected(undefined);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <QmsUserSelect
          value={selected}
          onChange={setSelected}
          placeholder="Select CCB member"
          ariaLabel="Change Control Board member"
          className="flex-1"
        />
        <Button type="button" variant="outline" onClick={addSelected} disabled={!selected}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add
        </Button>
      </div>
      {memberIds.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {memberIds.map((id) => (
            <MemberChip
              key={id}
              id={id}
              onRemove={() => onChange(memberIds.filter((candidate) => candidate !== id))}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-amber-700">
          Major and critical changes cannot be approved until every listed CCB member signs.
        </p>
      )}
    </div>
  );
}
